<script setup>
import HrFilterChip from './HrFilterChip.vue';

/**
 * A set of filter chips where the user picks one option, or several.
 *
 * HrFilterChip is an independent boolean per chip, which is right for a lone toggle but leaves
 * both the mutual exclusion of a single-select set and the array handling of a multi-select one
 * to every caller. This is to HrFilterChip what HrOptionGroup is to HrRadio and HrCheckbox.
 *
 * @status recommended
 * @group chips
 * @useWhen Choosing one option from a short, self-explanatory set, such as a comparison operator
 * @useWhen Choosing several values to filter by, where the options are few enough to show at once
 * @avoidWhen The set is long or loads asynchronously — a searchable list scans better than a wall of chips
 * @avoidWhen The options are actions rather than choices — use HrAssistChip
 * @avoidWhen Showing values already applied, which the user removes rather than deselects — use HrInputChip
 * @related HrFilterChip
 * @related HrOptionGroup
 * @a11y Each chip is its own control, so the set is reached with Tab rather than arrow keys.
 * Quasar activates a chip on Enter but not Space.
 */
defineOptions({ name: 'HrFilterChipGroup' });

const props = defineProps({
  /**
   * Selected value, or the array of selected values when `multiple` is set. Matched against each
   * option's `value` by identity, so `null` is a selectable value rather than an empty selection.
   */
  modelValue: {
    type: [String, Number, Boolean, Array],
    default: null,
  },
  /**
   * Options to choose from. `disable` on an option disables that chip alone, and `icon` gives it a
   * leading icon, which the check replaces while the option is selected.
   *
   * Each `value` must be a primitive. Selection is matched by identity, so an object or array
   * value could only ever match the one reference it was created from, and a parent that rebuilds
   * its option list (any computed does) would drop the selection on the next evaluation.
   * @example [{"label":"Contains","value":"contains"},{"label":"Starts with","value":"startsWith"}]
   */
  options: {
    type: Array,
    required: true,
    /*
     * A chip is keyed on its value, so the value has to be something two distinct options cannot
     * share a rendering of. Listed rather than tested with `typeof !== 'object'`, which also let
     * undefined, symbols and functions through: two symbols with the same description key
     * identically, and Vue then reuses a chip for the wrong option. Inlined because defineProps
     * is hoisted out of the setup scope and cannot reach a const declared beside it.
     */
    validator: (options) => options.every(
      (option) => option !== null
        && typeof option === 'object'
        && (option.value === null
          || ['string', 'number', 'boolean'].includes(typeof option.value)),
    ),
  },
  /** Allows several options at once, making `modelValue` an array. */
  multiple: {
    type: Boolean,
    default: false,
  },
  /**
   * Single-select only: whether activating the selected chip clears the selection. Off by default,
   * so a set that must always have an answer cannot be emptied by a stray tap. Multi-select always
   * allows deselecting, since an empty set is a meaningful state there.
   *
   * Clearing emits `null`, so a set that offers `null` as an option value cannot express "nothing
   * selected" and must not be made clearable.
   */
  clearable: {
    type: Boolean,
    default: false,
  },
  /**
   * Chip size, applied to every chip in the set.
   * @values sm, md, lg
   */
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg'].includes(v),
  },
  /** Prevents interaction and dims every chip. */
  disable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Fired when the selection changes; payload is the new value, or the new array when `multiple`. */
  'update:modelValue': null,
});

/*
 * Typed, because the values are matched by identity and String() would give 1 and '1' the same key.
 * Vue would then treat two distinct options as one and can carry a chip's state onto the wrong one.
 *
 * Sound only because an option value is a primitive, which the prop validator holds to. No
 * serialisation could stand in for that: two equal-content objects are one key and two values, and
 * the reverse of what identity matching would do with them.
 */
const optionKey = (option) => `${typeof option.value}:${String(option.value)}`;

const selectedValues = () => (Array.isArray(props.modelValue) ? props.modelValue : []);

function isSelected(option) {
  return props.multiple
    ? selectedValues().includes(option.value)
    : option.value === props.modelValue;
}

function toggleMultiple(option) {
  const current = selectedValues();

  emit('update:modelValue', isSelected(option)
    ? current.filter((value) => value !== option.value)
    : [...current, option.value]);
}

function toggleSingle(option) {
  if (!isSelected(option)) {
    emit('update:modelValue', option.value);
    return;
  }

  if (props.clearable) {
    emit('update:modelValue', null);
  }
}

function onToggle(option) {
  if (props.multiple) {
    toggleMultiple(option);
  } else {
    toggleSingle(option);
  }
}
</script>

<template>
  <div class="hr-filter-chip-group">
    <hr-filter-chip
      v-for="option in options"
      :key="optionKey(option)"
      :model-value="isSelected(option)"
      :label="option.label"
      :icon="option.icon ?? null"
      :size="size"
      :disable="disable || option.disable === true"
      @update:model-value="onToggle(option)"
    />
  </div>
</template>

<style lang="scss" scoped>
.hr-filter-chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: var(--app-comp-chip-group-gap, 8px);
}
</style>
